import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Save, Sparkles, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { TagInput } from "@/components/TagInput";

export default function AutofillProductReview() {
  const { t } = useTranslation(["autofill", "products", "common", "errors"]);
  const location = useLocation();
  const navigate = useNavigate();
  const draftIn = location.state?.draft;
  const brandId = location.state?.brand_id;
  const brandName = location.state?.brand_name;
  const sourceUrl = location.state?.source_url;

  const [draft, setDraft] = useState(draftIn || null);
  const [saving, setSaving] = useState(false);

  if (!draft || !brandId) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white px-8 py-16 text-center" data-testid="autofill-product-review-empty">
        <h2 className="font-display text-xl font-semibold text-slate-900">{t("autofill:review.noDraft.title")}</h2>
        <p className="mt-2 text-sm text-slate-500">{t("autofill:review.noDraft.body")}</p>
        <Button onClick={() => navigate(brandId ? `/brands/${brandId}` : "/brands")} className="mt-6 bg-slate-900 text-white hover:bg-slate-800">
          {t("autofill:review.noDraft.cta")}
        </Button>
      </div>
    );
  }

  const save = async () => {
    if (!draft.name?.trim()) {
      toast.error(t("errors:validation"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/products/from_autofill", { brand_id: brandId, draft });
      toast.success(t("autofill:results.productCreated"));
      navigate(`/products/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="autofill-product-review-page">
      <button
        type="button"
        onClick={() => navigate(`/brands/${brandId}`)}
        data-testid="autofill-product-review-back"
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("common:back")}
      </button>

      <header className="space-y-3">
        <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
          <Sparkles className="mr-1 h-3 w-3" strokeWidth={2} /> {t("autofill:aiBadge")}
        </Badge>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {t("autofill:review.product.title")}
        </h1>
        <p className="max-w-2xl text-sm text-slate-500">
          {t("autofill:review.product.subtitle", { brand: brandName || "—" })}
        </p>
      </header>

      <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.nameLabel")}</Label>
            <Input required value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="autofill-product-name" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.categoryLabel")}</Label>
            <Input value={draft.category || ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="autofill-product-category" className="h-11" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.urlLabel")}</Label>
          <Input type="url" value={draft.url || ""} onChange={(e) => setDraft({ ...draft, url: e.target.value })} data-testid="autofill-product-url" className="h-11" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.featuresLabel")}</Label>
            <TagInput value={draft.features || []} onChange={(v) => setDraft({ ...draft, features: v })} testId="autofill-product-features" placeholder={t("products:form.featuresHint")} />
            <p className="text-xs text-slate-400">{t("products:form.featuresHint")}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.benefitsLabel")}</Label>
            <TagInput value={draft.benefits || []} onChange={(v) => setDraft({ ...draft, benefits: v })} testId="autofill-product-benefits" placeholder={t("products:form.benefitsHint")} />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.pricingLabel")}</Label>
          <Textarea value={draft.pricing || ""} onChange={(e) => setDraft({ ...draft, pricing: e.target.value })} data-testid="autofill-product-pricing" rows={3} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.icpLabel")}</Label>
          <Textarea value={draft.icp || ""} onChange={(e) => setDraft({ ...draft, icp: e.target.value })} data-testid="autofill-product-icp" rows={3} />
        </div>
      </div>

      {(draft.sources || []).length > 0 && (
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <header className="space-y-1">
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{t("autofill:review.sources.title")}</h2>
            <p className="text-sm text-slate-500">{t("autofill:review.sources.subtitle")}</p>
          </header>
          <div className="space-y-2">
            {draft.sources.map((p, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                <FileSearch className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <a href={p.url} target="_blank" rel="noreferrer" className="block truncate text-sm font-medium text-indigo-700 hover:text-indigo-900">
                    {p.title || p.url}
                  </a>
                  {p.excerpt && <p className="mt-1 text-xs text-slate-500">{p.excerpt}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={() => navigate(`/brands/${brandId}`)} data-testid="autofill-product-review-discard">
          {t("autofill:review.discard")}
        </Button>
        <Button onClick={save} disabled={saving} data-testid="autofill-product-review-save" className="bg-slate-900 text-white hover:bg-slate-800">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" strokeWidth={1.75} />{t("autofill:review.saveProduct")}</>}
        </Button>
      </div>
    </div>
  );
}
